import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Container,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Divider,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  CloudQueue as CloudIcon,
  Computer as ServerIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import { selectAdminInfo } from "../store/systemData";
import AdminPage from "./AdminPage";
import { apiClient } from "../utils/apiClient";
import { getDashboardPages } from "./adminPagesConfig";

interface OperationHistoryItem {
  timestamp: string;
  type: string;
  status: string;
  targetedResource?: string;
}

interface InfrastructureStatus {
  environment: string;
  apiService: {
    status: string;
    currentImageUri: string;
    imageBuiltAt: string | null;
    lastDeploymentAt: string | null;
    rollbackStatus?: string;
    recentOperations: OperationHistoryItem[];
    error?: string;
  };
  workerService: {
    status: string;
    runningTasks: number;
    desiredTasks: number;
    currentImageUri: string;
    imageBuiltAt: string | null;
    lastDeploymentAt: string | null;
    taskDefinitionRevision: number;
    recentOperations: OperationHistoryItem[];
    error?: string;
  };
}

export function AdminDashboard() {
  const navigate = useNavigate();
  const adminInfo = useSelector(selectAdminInfo);
  const [infrastructureStatus, setInfrastructureStatus] =
    useState<InfrastructureStatus | null>(null);
  const [infrastructureLoading, setInfrastructureLoading] = useState(true);
  const [infrastructureError, setInfrastructureError] = useState<string | null>(
    null
  );

  // Fetch infrastructure status
  const fetchInfrastructureStatus = async () => {
    try {
      setInfrastructureLoading(true);
      setInfrastructureError(null);
      const response = await apiClient.post<InfrastructureStatus>(
        "/admin/infrastructure/status",
        {},
        { withCredentials: true }
      );
      setInfrastructureStatus(response);
    } catch (error) {
      console.error("Failed to fetch infrastructure status:", error);
      setInfrastructureError("Failed to load infrastructure status");
    } finally {
      setInfrastructureLoading(false);
    }
  };

  // Auto-refresh infrastructure status
  useEffect(() => {
    fetchInfrastructureStatus();

    // Refresh every 30 seconds
    const interval = setInterval(fetchInfrastructureStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  // Format timestamp for display
  const formatTimestamp = (isoString: string | null): string => {
    if (!isoString) return "Unknown";
    return new Date(isoString).toLocaleString();
  };

  // Get status color
  const getStatusColor = (status: string, hasError?: boolean) => {
    if (hasError) return "error";
    switch (status.toLowerCase()) {
      case "running":
      case "active":
        return "success";
      case "operation_in_progress":
      case "draining":
        return "warning";
      default:
        return "error";
    }
  };

  // Get all pages from the shared config
  const adminSections = getDashboardPages();

  return (
    <AdminPage>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Box sx={{ mb: 2 }}>
            <img
              src="/www/assets/dobby_login_hero.png"
              alt="Dobby Logo"
              style={{
                height: 120,
                objectFit: "contain",
              }}
            />
          </Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Admin Dashboard
          </Typography>
        </Box>

        <Paper>
          <List>
            {adminSections.map((page, index) => (
              <React.Fragment key={page.key}>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => navigate(page.path)}>
                    <ListItemIcon>{page.icon}</ListItemIcon>
                    <ListItemText
                      primary={page.title}
                      secondary={page.description}
                    />
                  </ListItemButton>
                </ListItem>
                {index < adminSections.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>

        {/* Infrastructure Status Section */}
        <Box sx={{ mt: 3 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography variant="h5" component="h2">
              Infrastructure Status
            </Typography>
            <Tooltip title="Refresh Infrastructure Status">
              <IconButton
                onClick={fetchInfrastructureStatus}
                disabled={infrastructureLoading}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {infrastructureLoading && !infrastructureStatus ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : infrastructureError ? (
            <Paper sx={{ p: 2, bgcolor: "error.main", color: "white" }}>
              <Typography variant="body1">
                <ErrorIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                {infrastructureError}
              </Typography>
            </Paper>
          ) : infrastructureStatus ? (
            <Grid container spacing={2}>
              {/* API Service Status */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <CloudIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">
                        API Service (App Runner)
                      </Typography>
                    </Box>

                    <Box mb={2}>
                      <Chip
                        icon={
                          infrastructureStatus.apiService.error ? (
                            <ErrorIcon />
                          ) : (
                            <CheckIcon />
                          )
                        }
                        label={
                          infrastructureStatus.apiService.error
                            ? "Error"
                            : infrastructureStatus.apiService.status
                        }
                        color={getStatusColor(
                          infrastructureStatus.apiService.status,
                          !!infrastructureStatus.apiService.error
                        )}
                        variant="filled"
                      />
                    </Box>

                    {infrastructureStatus.apiService.error ? (
                      <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                        {infrastructureStatus.apiService.error}
                      </Typography>
                    ) : (
                      <>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          <strong>Image Built:</strong>{" "}
                          {formatTimestamp(
                            infrastructureStatus.apiService.imageBuiltAt
                          )}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          <strong>Last Deployment:</strong>{" "}
                          {formatTimestamp(
                            infrastructureStatus.apiService.lastDeploymentAt
                          )}
                        </Typography>
                        {infrastructureStatus.apiService.rollbackStatus && (
                          <Typography
                            variant="body2"
                            color="warning.main"
                            gutterBottom
                          >
                            <strong>Rollback:</strong>{" "}
                            {infrastructureStatus.apiService.rollbackStatus}
                          </Typography>
                        )}
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ wordBreak: "break-all" }}
                        >
                          <strong>Image:</strong>{" "}
                          {infrastructureStatus.apiService.currentImageUri
                            .split("/")
                            .pop() || "Unknown"}
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Worker Service Status */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <ServerIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">
                        Temporal Worker (ECS)
                      </Typography>
                    </Box>

                    <Box mb={2}>
                      <Chip
                        icon={
                          infrastructureStatus.workerService.error ? (
                            <ErrorIcon />
                          ) : (
                            <CheckIcon />
                          )
                        }
                        label={
                          infrastructureStatus.workerService.error
                            ? "Error"
                            : infrastructureStatus.workerService.status
                        }
                        color={getStatusColor(
                          infrastructureStatus.workerService.status,
                          !!infrastructureStatus.workerService.error
                        )}
                        variant="filled"
                      />
                      <Chip
                        label={`${infrastructureStatus.workerService.runningTasks}/${infrastructureStatus.workerService.desiredTasks} Tasks`}
                        color={
                          infrastructureStatus.workerService.runningTasks ===
                          infrastructureStatus.workerService.desiredTasks
                            ? "success"
                            : "warning"
                        }
                        variant="outlined"
                        sx={{ ml: 1 }}
                      />
                    </Box>

                    {infrastructureStatus.workerService.error ? (
                      <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                        {infrastructureStatus.workerService.error}
                      </Typography>
                    ) : (
                      <>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          <strong>Image Built:</strong>{" "}
                          {formatTimestamp(
                            infrastructureStatus.workerService.imageBuiltAt
                          )}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          <strong>Last Deployment:</strong>{" "}
                          {formatTimestamp(
                            infrastructureStatus.workerService.lastDeploymentAt
                          )}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          <strong>Task Definition:</strong> Rev{" "}
                          {
                            infrastructureStatus.workerService
                              .taskDefinitionRevision
                          }
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ wordBreak: "break-all" }}
                        >
                          <strong>Image:</strong>{" "}
                          {infrastructureStatus.workerService.currentImageUri
                            .split("/")
                            .pop() || "Unknown"}
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Operations History */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <HistoryIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">Recent Operations</Typography>
                    </Box>

                    <Grid container spacing={2}>
                      {/* API Service Operations */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" gutterBottom>
                          <CloudIcon
                            sx={{
                              mr: 1,
                              verticalAlign: "middle",
                              fontSize: "1rem",
                            }}
                          />
                          API Service Operations
                        </Typography>
                        {infrastructureStatus.apiService.recentOperations
                          .length > 0 ? (
                          infrastructureStatus.apiService.recentOperations.map(
                            (op, index) => (
                              <Box
                                key={index}
                                sx={{
                                  mb: 1,
                                  p: 1,
                                  bgcolor: "grey.50",
                                  borderRadius: 1,
                                }}
                              >
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      fontWeight="medium"
                                    >
                                      {op.type.replace(/_/g, " ")}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {formatTimestamp(op.timestamp)}
                                    </Typography>
                                  </Box>
                                  <Chip
                                    size="small"
                                    label={op.status}
                                    color={
                                      op.status === "SUCCEEDED"
                                        ? "success"
                                        : op.status === "FAILED"
                                          ? "error"
                                          : "warning"
                                    }
                                  />
                                </Box>
                                {op.targetedResource && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    sx={{ mt: 0.5 }}
                                  >
                                    {op.targetedResource}
                                  </Typography>
                                )}
                              </Box>
                            )
                          )
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No recent operations
                          </Typography>
                        )}
                      </Grid>

                      {/* Worker Service Operations */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" gutterBottom>
                          <ServerIcon
                            sx={{
                              mr: 1,
                              verticalAlign: "middle",
                              fontSize: "1rem",
                            }}
                          />
                          Worker Service Events
                        </Typography>
                        {infrastructureStatus.workerService.recentOperations
                          .length > 0 ? (
                          infrastructureStatus.workerService.recentOperations.map(
                            (op, index) => (
                              <Box
                                key={index}
                                sx={{
                                  mb: 1,
                                  p: 1,
                                  bgcolor: "grey.50",
                                  borderRadius: 1,
                                }}
                              >
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      fontWeight="medium"
                                    >
                                      {op.type.replace(/_/g, " ")}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {formatTimestamp(op.timestamp)}
                                    </Typography>
                                  </Box>
                                  <Chip
                                    size="small"
                                    label={op.status}
                                    color={
                                      op.status === "SUCCEEDED"
                                        ? "success"
                                        : op.status === "FAILED"
                                          ? "error"
                                          : "warning"
                                    }
                                  />
                                </Box>
                                {op.targetedResource && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    sx={{ mt: 0.5, fontSize: "0.7rem" }}
                                  >
                                    {op.targetedResource}
                                  </Typography>
                                )}
                              </Box>
                            )
                          )
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No recent events
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : null}

          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 1, textAlign: "center" }}
          >
            Monitoring Environment:{" "}
            <strong>{infrastructureStatus?.environment || "Unknown"}</strong> •
            Auto-refreshes every 30 seconds
          </Typography>
        </Box>

        {/* Admin Info Section */}
        {adminInfo && (
          <Box sx={{ mt: 3, textAlign: "left" }}>
            <Paper sx={{ p: 2, bgcolor: "grey.100" }}>
              <Typography
                variant="body2"
                color="text.secondary"
                whiteSpace="pre"
              >
                {JSON.stringify(adminInfo, null, 2)}
              </Typography>
            </Paper>
          </Box>
        )}
      </Container>
    </AdminPage>
  );
}
